import React from 'react';
import { Card, CardContent, Typography, Box, Grid, Chip, CircularProgress } from '@mui/material';
import { TrendingUp, TrendingDown, PieChart } from '@mui/icons-material';
import { ConsolidatedHolding } from '../services/investments-service';
import { glassTile } from '../styles/glass';
import { formatCurrency } from '../utils/currency';
import { useBaseCurrency } from '../stores/currency-store';

interface PortfolioSummaryCardProps {
    holdings: ConsolidatedHolding[];
    isLoading: boolean;
}

export const PortfolioSummaryCard: React.FC<PortfolioSummaryCardProps> = ({ holdings, isLoading }) => {
    const totalValue = holdings.reduce((sum, h) => {
        // Mock market price logic again for consistency
        const mockMarketPrice = parseFloat(h.acb_per_share) * (1 + (Math.random() * 0.2 - 0.1));
        return sum + (mockMarketPrice * parseFloat(h.total_shares));
    }, 0);

    const totalCost = holdings.reduce((sum, h) => sum + parseFloat(h.total_cost_basis), 0);
    const totalGain = totalValue - totalCost;
    const totalGainPercent = totalCost > 0 ? (totalGain / totalCost) * 100 : 0;
    const isPositive = totalGain >= 0;

    // Was hardcoded to USD, which mislabelled every figure for a CAD household.
    const baseCurrency = useBaseCurrency();
    const fmt = (val: number) => formatCurrency(val, baseCurrency, { base: baseCurrency });

    return (
        <Card sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            Portfolio Performance
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Total value across all accounts
                        </Typography>
                    </Box>
                    <Box sx={{ p: 1, bgcolor: 'primary.50', borderRadius: 2, color: 'primary.main' }}>
                        <PieChart />
                    </Box>
                </Box>

                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={30} />
                    </Box>
                ) : holdings.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No investments found.
                    </Typography>
                ) : (
                    <>
                        <Typography variant="h3" fontWeight="bold" sx={{ mb: 1 }}>
                            {fmt(totalValue)}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                            <Chip
                                icon={isPositive ? <TrendingUp /> : <TrendingDown />}
                                label={`${isPositive ? '+' : ''}${fmt(totalGain)} (${totalGainPercent.toFixed(2)}%)`}
                                color={isPositive ? 'success' : 'error'}
                                size="small"
                                variant="filled"
                                sx={{ fontWeight: 'bold', bgcolor: isPositive ? 'success.light' : 'error.light', color: isPositive ? 'success.dark' : 'error.dark' }}
                            />
                            <Typography variant="caption" color="text.secondary">
                                All time
                            </Typography>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Box sx={{ p: 1.5, ...glassTile() }}>
                                    <Typography variant="caption" color="text.secondary">Invested</Typography>
                                    <Typography variant="subtitle2" fontWeight="bold">{fmt(totalCost)}</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{ p: 1.5, ...glassTile() }}>
                                    <Typography variant="caption" color="text.secondary">Holdings</Typography>
                                    <Typography variant="subtitle2" fontWeight="bold">{holdings.length}</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </>
                )}
            </CardContent>
        </Card>
    );
};
